import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Chip, Spinner, Typography } from '@glplus/glpweb-dsl';
import { RunnerPageHeading } from '../components/RunnerPageHeading';
import { RunnerAction } from '../components/RunnerAction';
import { Panel } from '../components/Panel';
import { runnerApi, RunnerApiError, type EarningsResponse, type Settlement } from '../lib/runnerApiClient';
import { formatCents, formatDeduction, formatSigned } from '../lib/money';

/**
 * §14 step 10 — "Your splits". The read side of the Runner ledger.
 *
 * 🛑 Nothing on this screen is computed from a run, an order or a rate card.
 * Every number is a row runner-api wrote, or a sum of pay items it holds. The
 * lane pass in particular is NOT re-derived here from a delivery count — it is
 * decided at settlement (grace, time off, the $60 cap) and the statement is the
 * only place that decision exists. A screen that could "estimate" it would
 * eventually show a Runner a number their deposit then contradicts.
 *
 * 🔑 The week in flight is shown apart from statements and labelled as not yet
 * settled, so money that has been earned but not decided reads as "coming" —
 * never as "yours", and never as "$0".
 *
 * 🛑 A failed load renders the failure, not zeros. "$0" on an earnings screen is
 * a claim about money; a request that did not complete cannot make it.
 */

const GREEN = '#2E9E3A';
const NAVY = '#343F5B';

const STATUS_STYLE: Record<Settlement['status'], { bg: string; fg: string }> = {
  pending: { bg: '#fef3c7', fg: '#b45309' },
  paid: { bg: '#dcfce7', fg: '#15803d' },
  failed: { bg: '#fee2e2', fg: '#b91c1c' },
  void: { bg: '#eef2f6', fg: '#4b5563' },
};

function prettyDay(ymd: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric', timeZone: 'UTC' }).format(new Date(`${ymd}T00:00:00.000Z`));
}
function prettyDate(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric', year: 'numeric' }).format(new Date(iso));
}
export function periodLabel(s: Pick<Settlement, 'periodStart' | 'periodEnd'>, locale: string): string {
  return `${prettyDay(s.periodStart, locale)} – ${prettyDay(s.periodEnd, locale)}`;
}

export default function Earnings() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'en';
  const [data, setData] = useState<EarningsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [documentsError, setDocumentsError] = useState<string | null>(null);
  const [openingDocuments, setOpeningDocuments] = useState(false);
  const es = i18n.language.startsWith('es');
  async function documents() {
    if (openingDocuments) return; setOpeningDocuments(true); setDocumentsError(null);
    try { const result = await runnerApi.paymentDashboard(); window.location.assign(result.url); }
    catch (failure) { setDocumentsError(failure instanceof RunnerApiError ? failure.message : es ? 'No se pudieron abrir los documentos fiscales.' : 'Could not open tax documents.'); setOpeningDocuments(false); }
  }

  useEffect(() => {
    let cancelled = false;
    runnerApi
      .earnings()
      .then((d) => !cancelled && setData(d))
      .catch((e) => !cancelled && setError(e instanceof RunnerApiError ? e.message : t('earnings.loadFailed')));
    return () => {
      cancelled = true;
    };
  }, [t]);

  return (
    <Box data-testid="page-earnings">
      <RunnerPageHeading title={es ? 'Ganancias e impuestos' : 'Earnings & Tax Documents'} subtitle={t('earnings.subtitle')} topic="earnings" />


      {error ? (
        <Alert severity="error" data-testid="earnings-error">{error}</Alert>
      ) : data === null ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
          <Spinner />
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1.7fr' }, gap: 2.5, alignItems: 'start' }}>
          <InFlight u={data.unsettled} locale={locale} />
          <Panel title={t('earnings.statementsTitle')} accent={NAVY} dataTestId="earnings-statements">
            {data.settlements.length === 0 ? (
              <Typography data-testid="earnings-no-statements" sx={{ color: '#4b5563', fontSize: '14px' }}>{t('earnings.noStatements')}</Typography>
            ) : (
              <Box sx={{ display: 'grid', gap: 1.5 }}>
                {data.settlements.map((s) => (
                  <StatementRow key={s.id} s={s} locale={locale} />
                ))}
              </Box>
            )}
          </Panel>
        </Box>
      )}
      <Box sx={{ mt: 2.5, mb: 2.5 }}>
        <Panel title={es ? 'Documentos fiscales · 1099' : 'Tax documents · 1099'} accent={GREEN} dataTestId="earnings-tax-documents">
          <Typography>{es ? 'Consulta tus documentos emitidos por año fiscal de forma segura en Stripe. Los documentos están disponibles cuando se emiten.' : 'View issued documents by tax year securely in Stripe. Documents are available when issued.'}</Typography>
          {documentsError && <Alert severity="error" sx={{ mt: 2 }}>{documentsError}</Alert>}
          <Box sx={{ mt: 2 }}>
            <RunnerAction topic="earnings" buttonText={es ? 'Ver documentos fiscales' : 'View tax documents'} ariaLabel={es ? 'Ver documentos fiscales' : 'View tax documents'} disabled={openingDocuments} onClick={() => void documents()} />
          </Box>
        </Panel>
      </Box>
    </Box>
  );
}

function InFlight({ u, locale }: { u: EarningsResponse['unsettled']; locale: string }) {
  const { t } = useTranslation();
  // A display sum of three numbers the API returned — not a policy decision.
  const earned = u.feesCents + u.tipsCents + u.compCents;
  return (
    <Panel title={t('earnings.inFlightTitle')} accent={GREEN} dataTestId="earnings-in-flight">
      <Typography sx={{ color: '#5f6b7a', fontSize: '13px', mb: 1.5 }}>{t('earnings.inFlightNote')}</Typography>
      {u.deliveries === 0 && earned === 0 ? (
        <Typography data-testid="earnings-no-activity" sx={{ color: '#4b5563', fontSize: '14px' }}>{t('earnings.noActivity')}</Typography>
      ) : (
        <>
          <Line testId="earnings-unsettled-deliveries" label={t('earnings.deliveries')} value={String(u.deliveries)} />
          <Line label={t('earnings.fees')} value={formatCents(u.feesCents, locale)} />
          <Line label={t('earnings.tips')} value={formatCents(u.tipsCents, locale)} />
          {u.compCents > 0 && <Line label={t('earnings.credits')} value={formatCents(u.compCents, locale)} />}
          <Box sx={{ borderTop: '1px solid #eef2f6', mt: 1, pt: 1 }}>
            <Line label={t('earnings.earnedSoFar')} value={formatCents(earned, locale)} strong />
          </Box>
        </>
      )}
    </Panel>
  );
}

function StatementRow({ s, locale }: { s: Settlement; locale: string }) {
  const { t } = useTranslation();
  const style = STATUS_STYLE[s.status] ?? STATUS_STYLE.void;
  const statusLabel = s.status === 'paid' && s.paidAt ? t('earnings.paidOn', { date: prettyDate(s.paidAt, locale) }) : t(`earnings.status.${s.status}`);
  return (
    <Box data-testid={`earnings-statement-${s.id}`} data-status={s.status} sx={{ border: '1px solid #e5e7eb', borderRadius: 2, px: 2, py: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: '15px', color: '#1f2937' }}>{periodLabel(s, locale)}</Typography>
          <Typography sx={{ color: '#5f6b7a', fontSize: '13px' }}>
            {s.deliveriesCount === 1 ? t('earnings.oneDelivery') : t('earnings.nDeliveries', { count: s.deliveriesCount })}
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography sx={{ fontWeight: 800, fontSize: '20px', color: '#1f2937', lineHeight: 1.1 }}>{formatCents(s.netCents, locale)}</Typography>
          <Chip label={statusLabel} size="small" backgroundColor={style.bg} textColor={style.fg} fontWeight={700} />
        </Box>
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' }, gap: 1, mt: 1.5, pt: 1.25, borderTop: '1px solid #f1f5f9' }}>
        <Cell label={t('earnings.fees')} value={formatCents(s.feesCents, locale)} />
        <Cell label={t('earnings.tips')} value={formatCents(s.tipsCents, locale)} />
        <Cell label={t('earnings.lanePass')} value={formatDeduction(s.lanePassCents, locale)} />
        <Cell label={t('earnings.adjustments')} value={formatSigned(s.adjustmentsCents, locale)} />
      </Box>
    </Box>
  );
}

function Line({ label, value, strong, testId }: { label: string; value: string; strong?: boolean; testId?: string }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, gap: 1 }}>
      <Typography sx={{ color: strong ? '#1f2937' : '#4b5563', fontWeight: strong ? 800 : 500, fontSize: '14px' }}>{label}</Typography>
      <Typography data-testid={testId} sx={{ color: '#1f2937', fontWeight: 800, fontSize: '14px' }}>{value}</Typography>
    </Box>
  );
}

function Cell({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography sx={{ color: '#5f6b7a', fontSize: '12px' }}>{label}</Typography>
      <Typography sx={{ color: '#1f2937', fontWeight: 700, fontSize: '14px' }}>{value}</Typography>
    </Box>
  );
}
